"use client";

/**
 * WhyVelyq — Asymmetric bento v3
 *
 * 3 cards:
 *  - HERO (2/3 × 2 rows): "Tout circule, rien ne se perd" with 4 mini product mockups
 *    orbiting a central Velyq hub. Real content inside each (CV, ATS 92%, lettre,
 *    interview live). Animated energy beams + data packets.
 *  - Right top:   "Pensé FR" with sector chips + sample question preview
 *  - Right bottom: "Voix IA" with waveform + price comparison
 */
import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import { motion } from "motion/react";
import { ArrowRight, Briefcase, Building2, Check, FileText, Layers, LineChart, Mail, MessageSquare, Mic, Search, Sparkles } from "lucide-react";

/* ──────────────────────────────────────────────
   Hero visual — 4 mini product mockups + central hub
   ────────────────────────────────────────────── */

function EcosystemShowcase() {
  const $ = _c(45);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="pointer-events-none absolute inset-0"><div className="absolute left-1/2 top-1/2 h-56 w-56 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/[0.18] blur-[80px]" /><div className="absolute left-1/4 top-1/4 h-32 w-32 rounded-full bg-[#5278ff]/[0.15] blur-[50px]" /><div className="absolute right-1/4 bottom-1/4 h-32 w-32 rounded-full bg-[#d946ef]/[0.12] blur-[50px]" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <linearGradient id="beam-1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#5278ff" stopOpacity="0" /><stop offset="50%" stopColor="#a855f7" stopOpacity="0.7" /><stop offset="100%" stopColor="#a855f7" stopOpacity="0" /></linearGradient>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <linearGradient id="beam-2" x1="100%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stopColor="#a855f7" stopOpacity="0" /><stop offset="50%" stopColor="#c084fc" stopOpacity="0.7" /><stop offset="100%" stopColor="#a855f7" stopOpacity="0" /></linearGradient>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <linearGradient id="beam-3" x1="0%" y1="100%" x2="100%" y2="0%"><stop offset="0%" stopColor="#d946ef" stopOpacity="0" /><stop offset="50%" stopColor="#a855f7" stopOpacity="0.7" /><stop offset="100%" stopColor="#a855f7" stopOpacity="0" /></linearGradient>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <defs>{t1}{t2}{t3}<linearGradient id="beam-4" x1="100%" y1="100%" x2="0%" y2="0%"><stop offset="0%" stopColor="#a855f7" stopOpacity="0" /><stop offset="50%" stopColor="#818cf8" stopOpacity="0.7" /><stop offset="100%" stopColor="#5278ff" stopOpacity="0" /></linearGradient></defs>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <motion.line x1="16" y1="20" x2="50" y2="50" stroke="url(#beam-1)" strokeWidth="0.8" initial={{
      pathLength: 0
    }} whileInView={{
      pathLength: 1
    }} viewport={{
      once: true
    }} transition={{
      duration: 1,
      delay: 0.3
    }} />;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <motion.line x1="84" y1="20" x2="50" y2="50" stroke="url(#beam-2)" strokeWidth="0.8" initial={{
      pathLength: 0
    }} whileInView={{
      pathLength: 1
    }} viewport={{
      once: true
    }} transition={{
      duration: 1,
      delay: 0.4
    }} />;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <motion.line x1="16" y1="80" x2="50" y2="50" stroke="url(#beam-3)" strokeWidth="0.8" initial={{
      pathLength: 0
    }} whileInView={{
      pathLength: 1
    }} viewport={{
      once: true
    }} transition={{
      duration: 1,
      delay: 0.5
    }} />;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">{t4}{t5}{t6}{t7}<motion.line x1="84" y1="80" x2="50" y2="50" stroke="url(#beam-4)" strokeWidth="0.8" initial={{
        pathLength: 0
      }} whileInView={{
        pathLength: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 1,
        delay: 0.6
      }} /></svg>;
    t9 = [{
      from: [16, 20],
      delay: 0
    }, {
      from: [84, 20],
      delay: 0.8
    }, {
      from: [16, 80],
      delay: 1.6
    }, {
      from: [84, 80],
      delay: 2.4
    }].map(_temp);
    $[8] = t8;
    $[9] = t9;
  } else {
    t8 = $[8];
    t9 = $[9];
  }
  let t10;
  let t11;
  let t12;
  let t13;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      opacity: 0,
      y: 10,
      rotate: -4
    };
    t11 = {
      opacity: 1,
      y: 0,
      rotate: -3
    };
    t12 = {
      once: true
    };
    t13 = {
      duration: 0.5,
      delay: 0.2
    };
    $[10] = t10;
    $[11] = t11;
    $[12] = t12;
    $[13] = t13;
  } else {
    t10 = $[10];
    t11 = $[11];
    t12 = $[12];
    t13 = $[13];
  }
  let t14;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-[#5278ff] to-[#6366f1] text-[10px] font-bold text-white">MD</div>;
    $[14] = t14;
  } else {
    t14 = $[14];
  }
  let t15;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="flex items-center gap-2">{t14}<div className="min-w-0 leading-tight"><div className="truncate text-[11px] font-bold">Marie Dubois</div><div className="text-[9px] text-muted-foreground">CV · PM</div></div><FileText className="ml-auto h-3 w-3 text-[#818cf8]" /></div>;
    $[15] = t15;
  } else {
    t15 = $[15];
  }
  let t16;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <motion.div initial={t10} whileInView={t11} viewport={t12} transition={t13} className="absolute left-0 top-0 w-[38%] rounded-xl border border-[#5278ff]/30 bg-card/95 p-3 backdrop-blur-md shadow-[0_20px_40px_-16px_rgba(82,120,255,0.4)]">{t15}<div className="mt-2 space-y-1"><div className="h-1 w-full rounded-full bg-[#5278ff]/20" /><div className="h-1 w-4/5 rounded-full bg-[#5278ff]/15" /><div className="h-1 w-3/5 rounded-full bg-[#5278ff]/15" /></div></motion.div>;
    $[16] = t16;
  } else {
    t16 = $[16];
  }
  let t17;
  let t18;
  let t19;
  let t20;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      opacity: 0,
      y: 10,
      rotate: 4
    };
    t18 = {
      opacity: 1,
      y: 0,
      rotate: 3
    };
    t19 = {
      once: true
    };
    t20 = {
      duration: 0.5,
      delay: 0.3
    };
    $[17] = t17;
    $[18] = t18;
    $[19] = t19;
    $[20] = t20;
  } else {
    t17 = $[17];
    t18 = $[18];
    t19 = $[19];
    t20 = $[20];
  }
  let t21;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <div className="flex items-center justify-between"><span className="text-[9px] font-bold uppercase tracking-wider text-primary/80">Score ATS</span><Search className="h-3 w-3 text-primary" /></div>;
    $[21] = t21;
  } else {
    t21 = $[21];
  }
  let t22;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div className="mt-2 flex items-baseline gap-1"><span className="font-display text-2xl font-bold text-primary">92</span><span className="text-[10px] font-semibold text-primary/60">%</span></div>;
    $[22] = t22;
  } else {
    t22 = $[22];
  }
  let t23;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-primary/15"><motion.div initial={{
        width: 0
      }} whileInView={{
        width: "92%"
      }} viewport={{
        once: true
      }} transition={{
        duration: 1,
        delay: 0.8
      }} className="h-full rounded-full bg-gradient-to-r from-primary to-[#c084fc]" /></div>;
    $[23] = t23;
  } else {
    t23 = $[23];
  }
  let t24;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <motion.div initial={t17} whileInView={t18} viewport={t19} transition={t20} className="absolute right-0 top-0 w-[32%] rounded-xl border border-primary/30 bg-card/95 p-3 backdrop-blur-md shadow-[0_20px_40px_-16px_rgba(168,85,247,0.4)]">{t21}{t22}{t23}<div className="mt-1.5 flex items-center gap-1 text-[9px] text-emerald-400"><Check className="h-2.5 w-2.5" strokeWidth={3} />Compatible</div></motion.div>;
    $[24] = t24;
  } else {
    t24 = $[24];
  }
  let t25;
  let t26;
  let t27;
  let t28;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = {
      opacity: 0,
      y: -10,
      rotate: 3
    };
    t26 = {
      opacity: 1,
      y: 0,
      rotate: 2
    };
    t27 = {
      once: true
    };
    t28 = {
      duration: 0.5,
      delay: 0.4
    };
    $[25] = t25;
    $[26] = t26;
    $[27] = t27;
    $[28] = t28;
  } else {
    t25 = $[25];
    t26 = $[26];
    t27 = $[27];
    t28 = $[28];
  }
  let t29;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-gradient-to-br from-[#c084fc] to-[#a855f7]"><Mail className="h-3 w-3 text-white" /></div>;
    $[29] = t29;
  } else {
    t29 = $[29];
  }
  let t30;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <div className="flex items-center gap-2">{t29}<div className="min-w-0 leading-tight"><div className="truncate text-[10px] font-bold">Lettre</div><div className="text-[9px] text-muted-foreground">Stripe · PM</div></div></div>;
    $[30] = t30;
  } else {
    t30 = $[30];
  }
  let t31;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <motion.div initial={t25} whileInView={t26} viewport={t27} transition={t28} className="absolute bottom-0 left-0 w-[38%] rounded-xl border border-[#c084fc]/30 bg-card/95 p-3 backdrop-blur-md shadow-[0_20px_40px_-16px_rgba(192,132,252,0.4)]">{t30}<div className="mt-2 space-y-1"><div className="h-0.5 w-full rounded-full bg-[#c084fc]/20" /><div className="h-0.5 w-full rounded-full bg-[#c084fc]/20" /><div className="h-0.5 w-4/5 rounded-full bg-[#c084fc]/20" /><div className="h-0.5 w-2/5 rounded-full bg-[#c084fc]/15" /></div></motion.div>;
    $[31] = t31;
  } else {
    t31 = $[31];
  }
  let t32;
  let t33;
  let t34;
  let t35;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = {
      opacity: 0,
      y: -10,
      rotate: -4
    };
    t33 = {
      opacity: 1,
      y: 0,
      rotate: -3
    };
    t34 = {
      once: true
    };
    t35 = {
      duration: 0.5,
      delay: 0.5
    };
    $[32] = t32;
    $[33] = t33;
    $[34] = t34;
    $[35] = t35;
  } else {
    t32 = $[32];
    t33 = $[33];
    t34 = $[34];
    t35 = $[35];
  }
  let t36;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <div className="flex items-center gap-1.5"><span className="relative flex h-1.5 w-1.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#d946ef]/80" /><span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#d946ef]" /></span><span className="text-[9px] font-bold uppercase tracking-wider text-[#d946ef]">Live</span><Mic className="ml-auto h-3 w-3 text-[#d946ef]" /></div>;
    $[36] = t36;
  } else {
    t36 = $[36];
  }
  let t37;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <motion.div initial={t32} whileInView={t33} viewport={t34} transition={t35} className="absolute bottom-0 right-0 w-[32%] rounded-xl border border-[#d946ef]/30 bg-card/95 p-3 backdrop-blur-md shadow-[0_20px_40px_-16px_rgba(217,70,239,0.4)]">{t36}<div className="mt-2 flex h-8 items-end justify-between gap-0.5">{[0.4, 0.6, 0.8, 0.5, 0.9, 0.7, 0.4, 0.6, 0.8, 0.5, 0.3, 0.7].map(_temp2)}</div><div className="mt-1.5 text-[9px] font-medium text-muted-foreground">Entretien #3</div></motion.div>;
    $[37] = t37;
  } else {
    t37 = $[37];
  }
  let t38;
  let t39;
  let t40;
  let t41;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = {
      scale: 0,
      opacity: 0
    };
    t39 = {
      scale: 1,
      opacity: 1
    };
    t40 = {
      once: true
    };
    t41 = {
      duration: 0.6,
      delay: 0.7,
      type: "spring",
      damping: 15
    };
    $[38] = t38;
    $[39] = t39;
    $[40] = t40;
    $[41] = t41;
  } else {
    t38 = $[38];
    t39 = $[39];
    t40 = $[40];
    t41 = $[41];
  }
  let t42;
  let t43;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <div className="absolute inset-0 -m-6 rounded-full bg-primary/30 blur-xl" />;
    t43 = <div className="absolute inset-0 -m-3 rounded-full bg-[#5278ff]/20 blur-lg" />;
    $[42] = t42;
    $[43] = t43;
  } else {
    t42 = $[42];
    t43 = $[43];
  }
  let t44;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <div className="relative h-[300px] w-full sm:h-[340px]">{t0}{t8}{t9}{t16}{t24}{t31}{t37}<motion.div initial={t38} whileInView={t39} viewport={t40} transition={t41} className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2">{t42}{t43}<div className="relative flex h-20 w-20 items-center justify-center rounded-2xl border border-primary/50 bg-gradient-to-br from-primary/30 via-[#5278ff]/25 to-[#a855f7]/30 backdrop-blur-md shadow-[0_0_48px_rgba(168,85,247,0.6),inset_0_1px_0_rgba(255,255,255,0.15)]"><Image src="/velyq_icon.png" alt="Velyq" width={48} height={48} className="h-12 w-12 object-contain drop-shadow-[0_0_10px_rgba(168,85,247,0.6)]" loading="lazy" /></div></motion.div></div>;
    $[44] = t44;
  } else {
    t44 = $[44];
  }
  return t44;
}

/* ──────────────────────────────────────────────
   Right card visuals
   ────────────────────────────────────────────── */
function _temp2(h, idx) {
  return <motion.div key={idx} initial={{
    scaleY: 0.2
  }} whileInView={{
    scaleY: h
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.4,
    delay: 0.7 + idx * 0.04
  }} style={{
    transformOrigin: "bottom"
  }} className="w-full rounded-full bg-gradient-to-t from-[#d946ef] to-[#c084fc]" />;
}
function _temp(p, i) {
  return <motion.div key={i} className="pointer-events-none absolute h-2 w-2 rounded-full bg-primary shadow-[0_0_12px_#a855f7]" style={{
    left: `${p.from[0]}%`,
    top: `${p.from[1]}%`
  }} animate={{
    left: [`${p.from[0]}%`, "50%"],
    top: [`${p.from[1]}%`, "50%"],
    opacity: [0, 1, 1, 0],
    scale: [0.5, 1, 1, 0.5]
  }} transition={{
    duration: 1.8,
    delay: p.delay + 1.2,
    repeat: Infinity,
    repeatDelay: 4,
    ease: "easeInOut"
  }} />;
}
function SectorShowcase() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [{
      icon: Briefcase,
      label: "Tech",
      company: "Airbnb \xB7 Stripe",
      gradient: "from-[#5278ff] to-[#6366f1]"
    }, {
      icon: Building2,
      label: "Consulting",
      company: "BCG \xB7 McKinsey",
      gradient: "from-[#a855f7] to-[#7c3aed]"
    }, {
      icon: LineChart,
      label: "Finance",
      company: "BNP \xB7 SG",
      gradient: "from-[#c084fc] to-[#a855f7]"
    }];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const sectors = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="mt-4 space-y-1.5">{sectors.map(_temp3)}</div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function _temp3(s) {
  return <div key={s.label} className="flex items-center justify-between rounded-lg border border-[#5278ff]/20 bg-card/50 px-2.5 py-1.5 backdrop-blur-sm"><div className="flex items-center gap-2"><div className={`flex h-5 w-5 items-center justify-center rounded-md bg-gradient-to-br ${s.gradient}`}><s.icon className="h-2.5 w-2.5 text-white" /></div><span className="text-[11px] font-semibold text-foreground">{s.label}</span></div><span className="text-[10px] font-medium text-muted-foreground/70">{s.company}</span></div>;
}
function VoiceShowcase() {
  const $ = _c(6);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="rounded-lg border border-[#d946ef]/25 bg-[#d946ef]/[0.06] px-3 py-2 backdrop-blur-sm"><div className="mb-1 flex items-center gap-1.5"><div className="flex h-4 w-4 items-center justify-center rounded-full bg-gradient-to-br from-[#d946ef] to-[#a855f7]"><Sparkles className="h-2 w-2 text-white" /></div><span className="text-[9px] font-bold uppercase tracking-wider text-[#d946ef]/90">IA · Recruteur</span></div><p className="text-[11px] leading-snug text-foreground/90">« Parle-moi d'une situation où tu as géré un conflit. »</p></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="mb-1.5 flex items-center justify-between"><span className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-wider text-[#d946ef]"><span className="relative flex h-1.5 w-1.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#d946ef]/80" /><span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#d946ef]" /></span>Toi · en direct</span><span className="text-[9px] font-medium text-muted-foreground/60">0:24</span></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="rounded-lg border border-[#d946ef]/40 bg-gradient-to-br from-[#d946ef]/[0.12] to-[#d946ef]/[0.04] px-3 py-2">{t1}<div className="flex h-6 items-center gap-0.5">{[0.3, 0.5, 0.8, 0.6, 0.9, 0.7, 0.5, 0.8, 0.6, 0.4, 0.7, 0.5, 0.8, 0.6, 0.4, 0.3, 0.6, 0.8, 0.5, 0.7].map(_temp4)}</div></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="flex flex-wrap gap-1"><span className="inline-flex items-center gap-1 rounded-full border border-emerald-400/30 bg-emerald-400/[0.08] px-1.5 py-0.5 text-[9px] font-semibold text-emerald-300"><Check className="h-2.5 w-2.5" strokeWidth={3} />Clarté 92%</span><span className="inline-flex items-center gap-1 rounded-full border border-[#818cf8]/30 bg-[#818cf8]/[0.08] px-1.5 py-0.5 text-[9px] font-semibold text-[#818cf8]">STAR ✓</span><span className="inline-flex items-center gap-1 rounded-full border border-yellow-400/30 bg-yellow-400/[0.08] px-1.5 py-0.5 text-[9px] font-semibold text-yellow-300">Filler 3×</span></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="text-[10px] text-muted-foreground/70">Concurrents{" "}<span className="font-semibold text-muted-foreground/50 line-through decoration-[#d946ef]/60">148€+</span></span>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="mt-4 space-y-2.5">{t0}{t2}{t3}<div className="flex items-center justify-between border-t border-[#d946ef]/15 pt-2">{t4}<span className="flex items-baseline gap-1"><span className="text-[9px] font-medium text-muted-foreground/60">Velyq</span><span className="text-[13px] font-bold text-[#d946ef]">9,99€</span></span></div></div>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
}

/* ──────────────────────────────────────────────
   Main
   ────────────────────────────────────────────── */
function _temp4(h, i) {
  return <motion.div key={i} initial={{
    scaleY: 0.2
  }} whileInView={{
    scaleY: h
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.4,
    delay: 0.4 + i * 0.02
  }} style={{
    transformOrigin: "center",
    height: "100%"
  }} className="w-0.5 flex-1 rounded-full bg-gradient-to-t from-[#d946ef] to-[#c084fc]" />;
}
interface WhyVelyqProps {
  locale: string;
}
export function WhyVelyq(t0) {
  const $ = _c(99);
  const {
    locale
  } = t0;
  const t1 = locale === "fr" ? "Ce qui nous diff\xE9rencie" : "What sets us apart";
  const t2 = locale === "fr" ? "Un \xE9cosyst\xE8me, pas une collection d'outils." : "An ecosystem, not a toolset.";
  const t3 = locale === "fr" ? "ChatGPT + Canva + LinkedIn + un tableur = du copier-coller. Velyq fait circuler tes donn\xE9es entre 8 outils qui se nourrissent l'un l'autre." : "ChatGPT + Canva + LinkedIn + a spreadsheet = copy-paste chaos. Velyq flows your data across 8 tools that feed each other.";
  const t4 = locale === "fr" ? "\xC9cosyst\xE8me connect\xE9" : "Connected ecosystem";
  const t5 = locale === "fr" ? "Tout circule. Rien ne se perd." : "Everything flows.";
  const t6 = locale === "fr" ? "Ton CV alimente l'analyse ATS, la lettre de motivation, la pr\xE9pa entretien et ton LinkedIn. Un seul effort \u2014 r\xE9sultat partout." : "Your CV feeds ATS analysis, cover letters, interview prep and LinkedIn. One effort \u2014 results everywhere.";
  let t7;
  if ($[0] !== t4 || $[1] !== t5 || $[2] !== t6) {
    t7 = {
      eyebrow: t4,
      title: t5,
      body: t6
    };
    $[0] = t4;
    $[1] = t5;
    $[2] = t6;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  const t8 = locale === "fr" ? "Pens\xE9 pour la France" : "Built for France";
  const t9 = locale === "fr" ? "Modes sectoriels calibr\xE9s sur les entreprises fran\xE7aises et leur culture du travail." : "Sector modes tuned to French companies and their work culture.";
  let t10;
  if ($[4] !== t8 || $[5] !== t9) {
    t10 = {
      title: t8,
      body: t9
    };
    $[4] = t8;
    $[5] = t9;
    $[6] = t10;
  } else {
    t10 = $[6];
  }
  const t11 = locale === "fr" ? "Parle. Bafouille. Recommence." : "Speak. Stumble. Retry.";
  const t12 = locale === "fr" ? "Une IA qui t'interrompt comme un vrai recruteur, note ta clart\xE9 en direct, et ne te juge pas." : "An AI that interrupts like a real recruiter, scores your clarity live, and won't judge you.";
  let t13;
  if ($[7] !== t11 || $[8] !== t12) {
    t13 = {
      title: t11,
      body: t12
    };
    $[7] = t11;
    $[8] = t12;
    $[9] = t13;
  } else {
    t13 = $[9];
  }
  let t14;
  if ($[10] !== t1 || $[11] !== t10 || $[12] !== t13 || $[13] !== t2 || $[14] !== t3 || $[15] !== t7) {
    t14 = {
      badge: t1,
      title: t2,
      subtitle: t3,
      hero: t7,
      fr: t10,
      voice: t13
    };
    $[10] = t1;
    $[11] = t10;
    $[12] = t13;
    $[13] = t2;
    $[14] = t3;
    $[15] = t7;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  const t = t14;
  let t15;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="pointer-events-none absolute inset-0 -z-10"><div className="absolute left-1/4 top-1/3 h-[400px] w-[400px] rounded-full bg-primary/[0.06] blur-[120px]" /><div className="absolute right-1/4 bottom-1/4 h-[320px] w-[320px] rounded-full bg-[#5278ff]/[0.05] blur-[100px]" /></div>;
    $[17] = t15;
  } else {
    t15 = $[17];
  }
  let t16;
  let t17;
  let t18;
  let t19;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      opacity: 0,
      y: 20
    };
    t17 = {
      opacity: 1,
      y: 0
    };
    t18 = {
      once: true,
      margin: "-80px"
    };
    t19 = {
      duration: 0.5
    };
    $[18] = t16;
    $[19] = t17;
    $[20] = t18;
    $[21] = t19;
  } else {
    t16 = $[18];
    t17 = $[19];
    t18 = $[20];
    t19 = $[21];
  }
  let t20;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <span className="h-1.5 w-1.5 rounded-full bg-primary" />;
    $[22] = t20;
  } else {
    t20 = $[22];
  }
  let t21;
  if ($[23] !== t.badge) {
    t21 = <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/[0.08] px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-primary">{t20}{t.badge}</span>;
    $[23] = t.badge;
    $[24] = t21;
  } else {
    t21 = $[24];
  }
  let t22;
  if ($[25] !== t.title) {
    t22 = <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-balance sm:text-4xl md:text-5xl">{t.title}</h2>;
    $[25] = t.title;
    $[26] = t22;
  } else {
    t22 = $[26];
  }
  let t23;
  if ($[27] !== t.subtitle) {
    t23 = <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">{t.subtitle}</p>;
    $[27] = t.subtitle;
    $[28] = t23;
  } else {
    t23 = $[28];
  }
  let t24;
  if ($[29] !== t21 || $[30] !== t22 || $[31] !== t23) {
    t24 = <motion.div initial={t16} whileInView={t17} viewport={t18} transition={t19} className="mb-12 text-center">{t21}{t22}{t23}</motion.div>;
    $[29] = t21;
    $[30] = t22;
    $[31] = t23;
    $[32] = t24;
  } else {
    t24 = $[32];
  }
  let t25;
  let t26;
  let t27;
  let t28;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = {
      opacity: 0,
      y: 30
    };
    t26 = {
      opacity: 1,
      y: 0
    };
    t27 = {
      once: true,
      margin: "-60px"
    };
    t28 = {
      duration: 0.5
    };
    $[33] = t25;
    $[34] = t26;
    $[35] = t27;
    $[36] = t28;
  } else {
    t25 = $[33];
    t26 = $[34];
    t27 = $[35];
    t28 = $[36];
  }
  let t29;
  let t30;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <div className="pointer-events-none absolute -top-24 -right-24 h-[280px] w-[280px] rounded-full bg-primary/[0.25] blur-[100px]" />;
    t30 = <div className="pointer-events-none absolute -bottom-24 -left-24 h-[220px] w-[220px] rounded-full bg-[#5278ff]/[0.15] blur-[90px]" />;
    $[37] = t29;
    $[38] = t30;
  } else {
    t29 = $[37];
    t30 = $[38];
  }
  let t31;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <Layers className="h-4 w-4 text-primary" />;
    $[39] = t31;
  } else {
    t31 = $[39];
  }
  let t32;
  if ($[40] !== t.hero.eyebrow) {
    t32 = <div className="flex items-center gap-2">{t31}<span className="text-[10px] font-bold uppercase tracking-wider text-primary">{t.hero.eyebrow}</span></div>;
    $[40] = t.hero.eyebrow;
    $[41] = t32;
  } else {
    t32 = $[41];
  }
  let t33;
  if ($[42] !== t.hero.title) {
    t33 = <h3 className="mt-3 font-display text-2xl font-bold leading-tight tracking-tight sm:text-3xl">{t.hero.title}</h3>;
    $[42] = t.hero.title;
    $[43] = t33;
  } else {
    t33 = $[43];
  }
  let t34;
  if ($[44] !== t.hero.body) {
    t34 = <p className="mt-2 max-w-md text-sm leading-relaxed text-muted-foreground sm:text-[15px]">{t.hero.body}</p>;
    $[44] = t.hero.body;
    $[45] = t34;
  } else {
    t34 = $[45];
  }
  let t35;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <div className="mt-6 flex-1"><EcosystemShowcase /></div>;
    $[46] = t35;
  } else {
    t35 = $[46];
  }
  let t36;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <Sparkles className="h-3.5 w-3.5 text-primary" />;
    $[47] = t36;
  } else {
    t36 = $[47];
  }
  const t37 = locale === "fr" ? "8 outils. Aucun copier-coller." : "8 tools. Zero copy-paste.";
  let t38;
  if ($[48] !== t37) {
    t38 = <div className="flex items-center gap-2">{t36}<span className="text-[12px] font-semibold text-primary">{t37}</span></div>;
    $[48] = t37;
    $[49] = t38;
  } else {
    t38 = $[49];
  }
  let t39;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <ArrowRight className="h-4 w-4 text-primary/60" />;
    $[50] = t39;
  } else {
    t39 = $[50];
  }
  let t40;
  if ($[51] !== t38) {
    t40 = <div className="mt-6 flex items-center justify-between">{t38}{t39}</div>;
    $[51] = t38;
    $[52] = t40;
  } else {
    t40 = $[52];
  }
  let t41;
  if ($[53] !== t32 || $[54] !== t33 || $[55] !== t34 || $[56] !== t40) {
    t41 = <motion.div initial={t25} whileInView={t26} viewport={t27} transition={t28} className="relative overflow-hidden rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/[0.08] via-card/70 to-card/50 p-6 backdrop-blur-sm shadow-[0_0_80px_-24px_rgba(168,85,247,0.5)] lg:col-span-2 lg:row-span-2 lg:p-8">{t29}{t30}<div className="relative flex h-full flex-col">{t32}{t33}{t34}{t35}{t40}</div></motion.div>;
    $[53] = t32;
    $[54] = t33;
    $[55] = t34;
    $[56] = t40;
    $[57] = t41;
  } else {
    t41 = $[57];
  }
  let t42;
  let t43;
  let t44;
  let t45;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = {
      opacity: 0,
      y: 30
    };
    t43 = {
      opacity: 1,
      y: 0
    };
    t44 = {
      once: true,
      margin: "-60px"
    };
    t45 = {
      duration: 0.5,
      delay: 0.1
    };
    $[58] = t42;
    $[59] = t43;
    $[60] = t44;
    $[61] = t45;
  } else {
    t42 = $[58];
    t43 = $[59];
    t44 = $[60];
    t45 = $[61];
  }
  let t46;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = <div className="pointer-events-none absolute -top-16 -right-16 h-[180px] w-[180px] rounded-full bg-[#5278ff]/[0.22] blur-[70px]" />;
    $[62] = t46;
  } else {
    t46 = $[62];
  }
  let t47;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <span className="rounded-md bg-gradient-to-br from-blue-600 to-blue-700 px-1.5 py-0.5 text-[10px] font-bold text-white shadow-sm">FR</span>;
    $[63] = t47;
  } else {
    t47 = $[63];
  }
  const t48 = locale === "fr" ? "March\xE9 local" : "Local market";
  let t49;
  if ($[64] !== t48) {
    t49 = <div className="flex items-center gap-2">{t47}<span className="text-[10px] font-bold uppercase tracking-wider text-[#818cf8]">{t48}</span></div>;
    $[64] = t48;
    $[65] = t49;
  } else {
    t49 = $[65];
  }
  let t50;
  if ($[66] !== t.fr.title) {
    t50 = <h3 className="mt-3 font-display text-lg font-bold leading-tight tracking-tight">{t.fr.title}</h3>;
    $[66] = t.fr.title;
    $[67] = t50;
  } else {
    t50 = $[67];
  }
  let t51;
  if ($[68] !== t.fr.body) {
    t51 = <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">{t.fr.body}</p>;
    $[68] = t.fr.body;
    $[69] = t51;
  } else {
    t51 = $[69];
  }
  let t52;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = <SectorShowcase />;
    $[70] = t52;
  } else {
    t52 = $[70];
  }
  let t53;
  if ($[71] !== t49 || $[72] !== t50 || $[73] !== t51) {
    t53 = <motion.div initial={t42} whileInView={t43} viewport={t44} transition={t45} className="relative overflow-hidden rounded-2xl border border-[#5278ff]/30 bg-gradient-to-br from-[#5278ff]/[0.08] via-card/70 to-card/50 p-6 backdrop-blur-sm shadow-[0_0_48px_-18px_rgba(82,120,255,0.4)]">{t46}<div className="relative">{t49}{t50}{t51}{t52}</div></motion.div>;
    $[71] = t49;
    $[72] = t50;
    $[73] = t51;
    $[74] = t53;
  } else {
    t53 = $[74];
  }
  let t54;
  let t55;
  let t56;
  let t57;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = {
      opacity: 0,
      y: 30
    };
    t55 = {
      opacity: 1,
      y: 0
    };
    t56 = {
      once: true,
      margin: "-60px"
    };
    t57 = {
      duration: 0.5,
      delay: 0.2
    };
    $[75] = t54;
    $[76] = t55;
    $[77] = t56;
    $[78] = t57;
  } else {
    t54 = $[75];
    t55 = $[76];
    t56 = $[77];
    t57 = $[78];
  }
  let t58;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = <div className="pointer-events-none absolute -bottom-16 -right-16 h-[180px] w-[180px] rounded-full bg-[#d946ef]/[0.2] blur-[70px]" />;
    $[79] = t58;
  } else {
    t58 = $[79];
  }
  let t59;
  if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
    t59 = <Mic className="h-4 w-4 text-[#d946ef]" />;
    $[80] = t59;
  } else {
    t59 = $[80];
  }
  const t60 = locale === "fr" ? "Simulation vocale live" : "Live voice sim";
  let t61;
  if ($[81] !== t60) {
    t61 = <div className="flex items-center gap-2">{t59}<span className="text-[10px] font-bold uppercase tracking-wider text-[#d946ef]">{t60}</span></div>;
    $[81] = t60;
    $[82] = t61;
  } else {
    t61 = $[82];
  }
  let t62;
  if ($[83] !== t.voice.title) {
    t62 = <h3 className="mt-3 font-display text-lg font-bold leading-tight tracking-tight">{t.voice.title}</h3>;
    $[83] = t.voice.title;
    $[84] = t62;
  } else {
    t62 = $[84];
  }
  let t63;
  if ($[85] !== t.voice.body) {
    t63 = <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground">{t.voice.body}</p>;
    $[85] = t.voice.body;
    $[86] = t63;
  } else {
    t63 = $[86];
  }
  let t64;
  if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
    t64 = <VoiceShowcase />;
    $[87] = t64;
  } else {
    t64 = $[87];
  }
  let t65;
  if ($[88] !== t61 || $[89] !== t62 || $[90] !== t63) {
    t65 = <motion.div initial={t54} whileInView={t55} viewport={t56} transition={t57} className="relative overflow-hidden rounded-2xl border border-[#d946ef]/30 bg-gradient-to-br from-[#d946ef]/[0.08] via-card/70 to-card/50 p-6 backdrop-blur-sm shadow-[0_0_48px_-18px_rgba(217,70,239,0.4)]">{t58}<div className="relative">{t61}{t62}{t63}{t64}</div></motion.div>;
    $[88] = t61;
    $[89] = t62;
    $[90] = t63;
    $[91] = t65;
  } else {
    t65 = $[91];
  }
  let t66;
  if ($[92] !== t41 || $[93] !== t53 || $[94] !== t65) {
    t66 = <div className="mx-auto grid max-w-5xl gap-4 lg:grid-cols-3 lg:grid-rows-2">{t41}{t53}{t65}</div>;
    $[92] = t41;
    $[93] = t53;
    $[94] = t65;
    $[95] = t66;
  } else {
    t66 = $[95];
  }
  let t67;
  if ($[96] !== t24 || $[97] !== t66) {
    t67 = <section className="relative overflow-hidden border-t border-border/30 py-20 sm:py-28">{t15}<div className="container relative mx-auto px-4">{t24}{t66}</div></section>;
    $[96] = t24;
    $[97] = t66;
    $[98] = t67;
  } else {
    t67 = $[98];
  }
  return t67;
}